import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, RefreshCw, ShieldAlert, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = 'all';

const EVENT_LABELS = {
  admin_login_success: 'Admin sign-in',
  admin_login_failed: 'Failed admin sign-in',
  admin_2fa_code_sent: 'Sign-in code sent',
  password_reset_requested: 'Reset requested',
  password_reset_completed: 'Password reset',
  admin_password_reset_link: 'Admin reset link',
  admin_password_reset_direct: 'Admin direct reset',
  data_export: 'Data export',
  admin_account_added: 'Admin added',
  admin_account_updated: 'Admin updated',
  admin_account_removed: 'Admin removed',
  admin_page_view: 'Admin page view',
  user_deletion_scheduled: 'Deletion scheduled',
  restore_requested: 'Restore requested',
  restore_declined: 'Restore declined',
  user_restored: 'User restored',
  user_deleted: 'User purged',
  trusted_device_revoked: 'Device revoked',
  billing_retry: 'Billing retry',
  testing_pass_created: 'Testing pass created',
  testing_pass_revoked: 'Testing pass revoked',
  testing_pass_used: 'Testing pass used',
};

const detailText = (d) => Object.entries(d || {}).map(([k, v]) => `${k}: ${v}`).join(' · ');

const LogRow = ({ log }) => (
  <tr className="border-b last:border-0 hover:bg-slate-50 transition-colors" data-testid={`access-log-row-${log.log_id}`}>
    <td className="py-2.5 pr-4 whitespace-nowrap text-xs text-gray-500">{new Date(log.created_at).toLocaleString()}</td>
    <td className="py-2.5 pr-4">
      <Badge variant={log.success ? 'secondary' : 'destructive'} className="whitespace-nowrap">
        {EVENT_LABELS[log.event_type] || log.event_type}
      </Badge>
    </td>
    <td className="py-2.5 pr-4 text-sm">{log.actor_email || '—'}{log.actor_is_admin && <span className="ml-1 text-[10px] uppercase text-emerald-700 font-semibold">admin</span>}</td>
    <td className="py-2.5 pr-4 text-sm">{log.target_email || '—'}</td>
    <td className="py-2.5 pr-4 text-xs text-gray-500 font-mono">{log.ip_address}</td>
    <td className="py-2.5 text-xs text-gray-600 max-w-xs truncate" title={detailText(log.details)}>{detailText(log.details) || '—'}</td>
  </tr>
);

const AccessLogTab = () => {
  const [filters, setFilters] = useState({ event_type: ALL, email: '', start: '', end: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setEmail(filters.email.trim()), 400);
    return () => clearTimeout(id);
  }, [filters.email]);

  // Dates are picked in local time; send exact UTC bounds
  const params = useCallback(() => {
    const p = {};
    if (filters.event_type !== ALL) p.event_type = filters.event_type;
    if (email) p.email = email;
    if (filters.start) p.start = new Date(`${filters.start}T00:00:00`).toISOString();
    if (filters.end) p.end = new Date(`${filters.end}T23:59:59.999`).toISOString();
    return p;
  }, [filters.event_type, filters.start, filters.end, email]);

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/access-log`, { params: { ...params(), page: pg, limit: 50 }, withCredentials: true });
      setData(res.data);
      setPage(pg);
    } catch {
      toast.error('Failed to load access log');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(1); }, [load]);

  const download = async () => {
    try {
      const res = await axios.get(`${API}/admin/access-log/export`, { params: params(), withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin-access-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('CSV download failed');
    }
  };

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <Card data-testid="access-log-tab-content">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Access Log</CardTitle>
          <CardDescription>Sign-ins, password resets, data exports, admin changes and admin page views.</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => load(page)} data-testid="access-log-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={download} data-testid="access-log-download-csv-btn">
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select value={filters.event_type} onValueChange={set('event_type')}>
            <SelectTrigger data-testid="access-log-event-filter"><SelectValue placeholder="All events" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All events</SelectItem>
              {Object.entries(EVENT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Filter by email" value={filters.email} onChange={(e) => set('email')(e.target.value)} data-testid="access-log-email-filter" />
          <Input type="date" value={filters.start} onChange={(e) => set('start')(e.target.value)} data-testid="access-log-start-date" />
          <Input type="date" value={filters.end} onChange={(e) => set('end')(e.target.value)} data-testid="access-log-end-date" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4 font-medium">Time</th><th className="py-2 pr-4 font-medium">Event</th>
                <th className="py-2 pr-4 font-medium">Actor</th><th className="py-2 pr-4 font-medium">Target</th>
                <th className="py-2 pr-4 font-medium">IP</th><th className="py-2 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>{data?.logs.map((log) => <LogRow key={log.log_id} log={log} />)}</tbody>
          </table>
          {data && data.logs.length === 0 && (
            <p className="text-center text-sm text-gray-500 py-8" data-testid="access-log-empty">No events match these filters.</p>
          )}
        </div>

        {data && (
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span data-testid="access-log-total">{data.total} events</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="access-log-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
              <span>Page {page} / {data.total_pages}</span>
              <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => load(page + 1)} data-testid="access-log-next-btn"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccessLogTab;
